import React, { useEffect, useMemo, useState, useRef } from 'react'
import { X, Trash2, Tag as TagIcon, AlertCircle, Check } from 'lucide-react'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { apiService } from '@/services/api'

interface ClassEditDialogProps {
  classId: string
  isOpen: boolean
  onClose: () => void
  onRenamed: (oldId: string, newId: string) => void
  onDeleted: (classId: string) => void
}

export const ClassEditDialog: React.FC<ClassEditDialogProps> = ({
  classId,
  isOpen,
  onClose,
  onRenamed,
  onDeleted,
}) => {
  const { categories, refreshCategories, refreshThumbnailForClass } = useCategoryContext()

  // Form state
  const [nameInput, setNameInput] = useState<string>(classId)
  const [originalName, setOriginalName] = useState<string>(classId)
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('')
  const [tag, setTag] = useState<string>('')
  const [originalTag, setOriginalTag] = useState<string>('')

  // UI state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingName, setSavingName] = useState(false)
  const [savingCategory, setSavingCategory] = useState(false)
  const [savingTag, setSavingTag] = useState(false)

  // Tag autocomplete state
  const [allTags, setAllTags] = useState<string[]>([])
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [tagHighlightIndex, setTagHighlightIndex] = useState(0)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  const categoryList = useMemo(() => Object.values(categories || {}), [categories])

  // Load initial data
  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setError(null)
    setNameInput(classId)
    setOriginalName(classId)

    Promise.all([
      apiService.getClassCategory(classId).catch(() => null),
      apiService.getClassTag(classId).catch(() => ({ tag: null })),
      apiService.getAllTags().catch(() => ({})),
    ])
      .then(([cat, tagResp, tagsData]) => {
        if (cat) {
          setSelectedCategoryName(cat.name)
        } else {
          setSelectedCategoryName('')
        }
        const currentTag = (tagResp as any)?.tag || ''
        setTag(currentTag)
        setOriginalTag(currentTag)

        // Extract unique tags from tagsData
        const uniqueTags = Object.keys(tagsData || {})
        setAllTags(uniqueTags)
      })
      .finally(() => setLoading(false))
  }, [isOpen, classId])

  // Filter tag suggestions based on input
  const tagSuggestions = useMemo(() => {
    if (!tag) return allTags
    const lower = tag.toLowerCase()
    return allTags.filter(t => t.toLowerCase().includes(lower) && t !== tag)
  }, [tag, allTags])

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(e.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(e.target as Node)
      ) {
        setShowTagSuggestions(false)
      }
    }

    if (showTagSuggestions) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTagSuggestions])

  // Handle rename (on blur or enter)
  const handleRename = async () => {
    if (!nameInput || nameInput === originalName) return

    try {
      setSavingName(true)
      setError(null)
      const result = await apiService.updateClass(classId as any, {
        folderName: nameInput,
        displayName: nameInput
      } as any)
      await refreshThumbnailForClass(nameInput)
      setOriginalName(nameInput)
      onRenamed(classId, result.id)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to rename class')
      setNameInput(originalName) // Revert on error
    } finally {
      setSavingName(false)
    }
  }

  // Handle category change (auto-save on change)
  const handleCategoryChange = async (newCategory: string) => {
    setSelectedCategoryName(newCategory)

    try {
      setSavingCategory(true)
      setError(null)

      if (!newCategory) {
        await apiService.removeClassCategory(classId)
      } else {
        const cat = categoryList.find(c => (c as any).name === newCategory)
        if (cat) {
          await apiService.setClassCategory(classId, {
            name: (cat as any).name,
            color: (cat as any).color,
            icon: (cat as any).icon
          } as any)
        }
      }
      // Force refresh to bypass cache and get fresh data from API
      await refreshCategories(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update category')
    } finally {
      setSavingCategory(false)
    }
  }

  // Handle tag save (on blur or enter)
  const handleSaveTag = async () => {
    if (tag === originalTag) return

    try {
      setSavingTag(true)
      setError(null)

      if (!tag.trim()) {
        await apiService.removeTag(classId)
      } else {
        await apiService.assignTag(classId, tag.trim())
      }
      setOriginalTag(tag.trim())
      setShowTagSuggestions(false)

      // Refresh tag list
      const tagsData = await apiService.getAllTags()
      setAllTags(Object.keys(tagsData || {}))
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update tag')
    } finally {
      setSavingTag(false)
    }
  }

  // Handle tag input
  const handleTagInputChange = (value: string) => {
    setTag(value)
    setShowTagSuggestions(true)
    setTagHighlightIndex(0)
  }

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (!showTagSuggestions || tagSuggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveTag()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setTagHighlightIndex((prev) =>
          prev < tagSuggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setTagHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : tagSuggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (tagSuggestions[tagHighlightIndex]) {
          setTag(tagSuggestions[tagHighlightIndex])
          setShowTagSuggestions(false)
        } else {
          handleSaveTag()
        }
        break
      case 'Escape':
        e.preventDefault()
        setShowTagSuggestions(false)
        break
    }
  }

  const selectTagSuggestion = (suggestion: string) => {
    setTag(suggestion)
    setShowTagSuggestions(false)
    tagInputRef.current?.focus()
  }

  // Handle delete
  const handleDelete = async () => {
    if (!confirm(`Delete class "${classId}"?\n\nThis will permanently remove:\n• The class folder and all images\n• All embeddings for this class\n• Any associations with sections\n\nThis action cannot be undone.`)) return

    try {
      await apiService.deleteClass(classId)
      onDeleted(classId)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete class')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Class</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              <Check className="h-4 w-4" />
              Save & Close
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close without saving"
              title="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Class Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class Name
            </label>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={handleRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
              disabled={savingName}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="Enter class name"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              {savingName ? 'Saving...' : 'Changes save automatically when you click away'}
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategoryName}
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={savingCategory}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">No category</option>
              {categoryList.map((cat: any) => (
                <option key={cat.name} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1.5">
              {savingCategory ? 'Saving...' : 'Select a category to organize this class'}
            </p>
          </div>

          {/* Tag with Autocomplete */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag
            </label>
            <div className="relative">
              <div className="relative">
                <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  ref={tagInputRef}
                  type="text"
                  value={tag}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={(e) => {
                    // Delay to allow click on suggestion
                    setTimeout(() => {
                      handleSaveTag()
                      setShowTagSuggestions(false)
                    }, 200)
                  }}
                  disabled={savingTag}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="e.g., Bracelets, Watches, Rings..."
                />
              </div>

              {/* Tag Suggestions Dropdown */}
              {showTagSuggestions && tagSuggestions.length > 0 && (
                <div
                  ref={tagDropdownRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                  {tagSuggestions.slice(0, 8).map((suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => selectTagSuggestion(suggestion)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        index === tagHighlightIndex
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <TagIcon className="h-3 w-3 text-gray-400" />
                        {suggestion}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              {savingTag ? 'Saving...' : 'Type to see existing tags or create a new one'}
            </p>
          </div>
        </div>

        {/* Footer - Danger Zone */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Delete Class</p>
              <p className="text-xs text-gray-500">Permanently remove this class and all its data</p>
            </div>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassEditDialog
